// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module EventTable
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS recent-events table for analytics
 * dashboards. The component formats event metadata into a compact grid and
 * includes loading skeletons plus an empty state for low-traffic views.
 */

import type { AnalyticsEvent, DeviceInfo, EventType } from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  EmptyState,
  Skeleton,
} from "@geenius/ui/solidjs-css";
import {
  type Component,
  createMemo,
  createSignal,
  For,
  type JSX,
  Show,
} from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

type EventTypeFilter = AnalyticsEvent["type"] | "all";
type EventTableSortKey = "name" | "type" | "url" | "device" | "timestamp";
type EventTableSortDirection = "asc" | "desc";
type EventTableAriaSort = "ascending" | "descending" | "none";
type EventTableFilterDevice = DeviceInfo["device"] | "all";

interface EventTableSortState {
  key: EventTableSortKey;
  direction: EventTableSortDirection;
}

const EVENT_TYPES: EventType[] = [
  "custom",
  "page_view",
  "click",
  "form_submit",
];

const DEVICE_TYPES: Array<DeviceInfo["device"]> = [
  "desktop",
  "tablet",
  "mobile",
];

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  click: "analytics.events.types.click",
  custom: "analytics.events.types.custom",
  form_submit: "analytics.events.types.formSubmit",
  page_view: "analytics.events.types.pageView",
};

function formatEventPathname(url: string): string {
  try {
    return new URL(url, "https://geenius.dev").pathname;
  } catch {
    return url;
  }
}

function stringifyEventProperties(properties: Record<string, unknown>): string {
  try {
    return JSON.stringify(properties, null, 2);
  } catch {
    return String(properties);
  }
}

function getSearchText(event: AnalyticsEvent): string {
  return [
    event.name,
    event.type,
    event.url,
    event.referrer,
    event.sessionId,
    event.userId ?? "",
    event.device.device,
    event.device.browser,
    event.device.os,
    stringifyEventProperties(event.properties ?? {}),
  ]
    .join(" ")
    .toLowerCase();
}

function getSortValue(
  event: AnalyticsEvent,
  key: EventTableSortKey,
): string | number {
  if (key === "device") return event.device.device;
  if (key === "timestamp") return event.timestamp;
  return event[key];
}

function sortEvents(
  events: AnalyticsEvent[],
  sort: EventTableSortState | null,
): AnalyticsEvent[] {
  if (!sort) return events;

  const direction = sort.direction === "asc" ? 1 : -1;
  return [...events].sort((left, right) => {
    const leftValue = getSortValue(left, sort.key);
    const rightValue = getSortValue(right, sort.key);

    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return (leftValue - rightValue) * direction;
    }

    return String(leftValue).localeCompare(String(rightValue)) * direction;
  });
}

function nextSortState(
  current: EventTableSortState | null,
  key: EventTableSortKey,
): EventTableSortState | null {
  if (current?.key !== key) {
    return { key, direction: key === "timestamp" ? "desc" : "asc" };
  }

  if (current.direction === "asc") {
    return { key, direction: "desc" };
  }

  return null;
}

function getAriaSort(
  sort: EventTableSortState | null,
  key: EventTableSortKey,
): EventTableAriaSort {
  if (sort?.key !== key) return "none";
  return sort.direction === "asc" ? "ascending" : "descending";
}

function hasEventProperties(event: AnalyticsEvent): boolean {
  return Object.keys(event.properties ?? {}).length > 0;
}

/**
 * Props for rendering the SolidJS CSS analytics event table.
 *
 * @property events Event rows to display in the table.
 * @property isLoading Whether the table should render loading skeleton rows.
 * @property maxRows Maximum number of event rows to render.
 * @property filterText Controlled search text used to filter event rows.
 * @property eventType Controlled event-type filter used to filter event rows.
 * @property showFilters Whether the table should render search and type controls.
 * @property class Optional class name merged into the wrapper.
 */
export interface EventTableProps {
  events: AnalyticsEvent[];
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  maxRows?: number;
  filterText?: string;
  eventType?: EventTypeFilter;
  device?: EventTableFilterDevice;
  onRetry?: () => void;
  onFilterTextChange?: (value: string) => void;
  onEventTypeChange?: (value: EventTypeFilter) => void;
  onDeviceChange?: (value: EventTableFilterDevice) => void;
  showFilters?: boolean;
  class?: string;
}

/**
 * Renders the SolidJS CSS analytics event table.
 *
 * @param props Event rows, loading state, and row limit for the table.
 * @returns An event table element with loading and empty states.
 */
export const EventTable: Component<EventTableProps> = (props) => {
  const [localFilterText, setLocalFilterText] = createSignal("");
  const [localEventType, setLocalEventType] =
    createSignal<EventTypeFilter>("all");
  const [localDevice, setLocalDevice] =
    createSignal<EventTableFilterDevice>("all");
  const [sort, setSort] = createSignal<EventTableSortState | null>(null);
  const [expandedEventIds, setExpandedEventIds] = createSignal<Set<string>>(
    new Set(),
  );
  const copy = createAnalyticsCopy();
  const filterText = () => props.filterText ?? localFilterText();
  const eventType = () => props.eventType ?? localEventType();
  const device = () => props.device ?? localDevice();
  const updateFilterText = (value: string): void => {
    props.onFilterTextChange?.(value);
    if (props.filterText === undefined) {
      setLocalFilterText(value);
    }
  };
  const updateEventType = (value: EventTypeFilter): void => {
    props.onEventTypeChange?.(value);
    if (props.eventType === undefined) {
      setLocalEventType(value);
    }
  };
  const updateDevice = (value: EventTableFilterDevice): void => {
    props.onDeviceChange?.(value);
    if (props.device === undefined) {
      setLocalDevice(value);
    }
  };
  const toggleEventProperties = (eventId: string): void => {
    setExpandedEventIds((current) => {
      const next = new Set(current);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };
  const isEventExpanded = (eventId: string): boolean =>
    expandedEventIds().has(eventId);
  const filtered = createMemo(() => {
    const search = filterText().trim().toLowerCase();
    const type = eventType();
    const deviceFilter = device();

    return props.events.filter((event) => {
      if (type !== "all" && event.type !== type) {
        return false;
      }

      if (deviceFilter !== "all" && event.device.device !== deviceFilter) {
        return false;
      }

      if (!search) {
        return true;
      }

      return getSearchText(event).includes(search);
    });
  });
  const displayed = () =>
    sortEvents(filtered(), sort()).slice(0, props.maxRows || 10);
  const renderSortableHeader = (
    key: EventTableSortKey,
    label: string,
  ): JSX.Element => (
    <th scope="col" aria-sort={getAriaSort(sort(), key)}>
      <button
        class={cx(
          "gn-analytics-event-table__sort-button",
          sort()?.key === key &&
            "gn-analytics-event-table__sort-button--active",
        )}
        type="button"
        onClick={() => setSort((current) => nextSortState(current, key))}
      >
        <span>{label}</span>
        <span
          class="gn-analytics-event-table__sort-indicator"
          aria-hidden="true"
        >
          {sort()?.key === key
            ? sort()?.direction === "asc"
              ? "ascending"
              : "descending"
            : "sort"}
        </span>
        <span class="gn-analytics-a11y__sr-only">
          {sort()?.key === key
            ? `Sorted ${sort()?.direction === "asc" ? "ascending" : "descending"}; activate to change sorting`
            : `Sort by ${label}`}
        </span>
      </button>
    </th>
  );

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardContent class="gn-analytics-event-table__shell">
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Show
      when={!props.isLoading}
      fallback={
        <Card class={props.class}>
          <CardContent
            class="gn-analytics-event-table__shell"
            data-analytics-table-loading
          >
            <For each={[1, 2, 3, 4, 5]}>{() => <Skeleton height="40px" />}</For>
          </CardContent>
        </Card>
      }
    >
      <Card class={props.class}>
        <CardContent class="gn-analytics-event-table__shell">
          <Show when={props.showFilters !== false}>
            <div
              class="gn-analytics-event-table__controls"
              data-analytics-table-controls
            >
              <label class="gn-analytics-event-table__control">
                <span>{copy.t("analytics.events.filters.search")}</span>
                <input
                  class="gn-analytics-event-table__filter-input"
                  type="search"
                  value={filterText()}
                  placeholder={copy.t(
                    "analytics.events.filters.searchPlaceholder",
                  )}
                  onInput={(event) =>
                    updateFilterText(event.currentTarget.value)
                  }
                />
              </label>
              <label class="gn-analytics-event-table__control">
                <span>{copy.t("analytics.events.filters.type")}</span>
                <select
                  class="gn-analytics-event-table__filter-select"
                  value={eventType()}
                  onChange={(event) =>
                    updateEventType(
                      event.currentTarget.value as EventTypeFilter,
                    )
                  }
                >
                  <option value="all">
                    {copy.t("analytics.events.filters.allTypes")}
                  </option>
                  <For each={EVENT_TYPES}>
                    {(type) => (
                      <option value={type}>
                        {copy.t(EVENT_TYPE_LABELS[type])}
                      </option>
                    )}
                  </For>
                </select>
              </label>
              <label class="gn-analytics-event-table__control">
                <span>{copy.t("analytics.events.filters.device")}</span>
                <select
                  class="gn-analytics-event-table__filter-select"
                  value={device()}
                  onChange={(event) =>
                    updateDevice(
                      event.currentTarget.value as EventTableFilterDevice,
                    )
                  }
                >
                  <option value="all">
                    {copy.t("analytics.events.filters.allDevices")}
                  </option>
                  <For each={DEVICE_TYPES}>
                    {(deviceType) => (
                      <option value={deviceType}>
                        {copy.deviceLabel(deviceType)}
                      </option>
                    )}
                  </For>
                </select>
              </label>
            </div>
          </Show>
          <Show
            when={displayed().length > 0}
            fallback={<EmptyState title={copy.t("analytics.events.empty")} />}
          >
            <table
              class="gn-analytics-event-table"
              data-analytics-table
              aria-label={copy.t("analytics.dashboard.recentEvents")}
            >
              <thead>
                <tr data-analytics-table-header>
                  {renderSortableHeader(
                    "name",
                    copy.t("analytics.events.columns.event"),
                  )}
                  {renderSortableHeader(
                    "type",
                    copy.t("analytics.events.columns.type"),
                  )}
                  {renderSortableHeader(
                    "url",
                    copy.t("analytics.events.columns.url"),
                  )}
                  {renderSortableHeader(
                    "device",
                    copy.t("analytics.events.columns.device"),
                  )}
                  {renderSortableHeader(
                    "timestamp",
                    copy.t("analytics.events.columns.time"),
                  )}
                </tr>
              </thead>
              <tbody>
                <For each={displayed()}>
                  {(event) => (
                    <>
                      <tr data-analytics-table-row>
                        <td
                          class="gn-analytics-event-table__event-cell"
                          data-analytics-cell-name
                        >
                          <span class="gn-analytics-event-table__event-name">
                            {event.name}
                          </span>
                          <Show when={hasEventProperties(event)}>
                            <button
                              class="gn-analytics-event-table__properties-button"
                              type="button"
                              aria-controls={`gn-analytics-event-properties-${event.id}`}
                              aria-expanded={isEventExpanded(event.id)}
                              aria-label={`${isEventExpanded(event.id) ? "Hide" : "Show"} properties for ${event.name}`}
                              onClick={() => toggleEventProperties(event.id)}
                            >
                              {isEventExpanded(event.id)
                                ? "Hide properties"
                                : "Show properties"}
                            </button>
                          </Show>
                        </td>
                        <td>
                          <Badge size="sm" variant="secondary">
                            <span
                              data-analytics-cell-type
                              data-type={event.type}
                            >
                              {event.type}
                            </span>
                          </Badge>
                        </td>
                        <td data-analytics-cell-url title={event.url}>
                          {formatEventPathname(event.url)}
                        </td>
                        <td
                          data-analytics-cell-device
                          data-device={event.device.device}
                        >
                          <span aria-hidden="true">
                            {event.device.device === "mobile"
                              ? "📱"
                              : event.device.device === "tablet"
                                ? "📟"
                                : "💻"}{" "}
                          </span>
                          {event.device.browser}
                        </td>
                        <td data-analytics-cell-time>
                          {copy.formatTime(event.timestamp)}
                        </td>
                      </tr>
                      <Show
                        when={
                          hasEventProperties(event) && isEventExpanded(event.id)
                        }
                      >
                        <tr
                          class="gn-analytics-event-table__properties-row"
                          data-analytics-table-properties
                          id={`gn-analytics-event-properties-${event.id}`}
                        >
                          <td
                            class="gn-analytics-event-table__properties-cell"
                            colSpan={5}
                          >
                            <pre class="gn-analytics-event-table__properties">
                              {stringifyEventProperties(event.properties ?? {})}
                            </pre>
                          </td>
                        </tr>
                      </Show>
                    </>
                  )}
                </For>
              </tbody>
            </table>
          </Show>
        </CardContent>
      </Card>
    </Show>
  );
};
